import { Client } from '@langchain/langgraph-sdk';
import { FormData, TenderAnalysisState } from '../types';

// Create LangGraph client
const client = new Client({
  apiUrl: process.env.REACT_APP_API_BASE_URL || 'https://api-beta-open-tender.nat-quest.com',
});

export interface StreamEvent {
  event: string;
  data: any;
  name?: string;
  metadata?: any;
}

export interface TenderAnalysisThread {
  thread_id: string;
  status: 'pending' | 'running' | 'success' | 'error';
  values?: TenderAnalysisState;
}

export class LangGraphTenderAPI {
  /**
   * Create a new thread for tender analysis
   */
  static async createThread(): Promise<{ thread_id: string }> {
    try {
      const thread = await client.threads.create();
      return { thread_id: thread.thread_id };
    } catch (error) {
      console.error('Error creating thread:', error);
      throw error;
    }
  }

  /**
   * Start tender analysis with streaming
   */
  static async streamTenderAnalysis(
    formData: FormData,
    onEvent: (event: StreamEvent) => void,
    onError: (error: any) => void,
    onComplete: (finalState: TenderAnalysisState) => void,
    userId?: string,
    onThreadCreated?: (thread_id: string) => void
  ): Promise<{ thread_id: string }> {
    console.log('LangGraphTenderAPI: streamTenderAnalysis called with:', formData);

    try {
      // Create a new thread
      console.log('LangGraphTenderAPI: Creating thread...');
      const { thread_id } = await this.createThread();
      console.log('LangGraphTenderAPI: Created thread with ID:', thread_id);
      // Expose the id to callers before we start the long-lived stream iteration
      // so UI effects (polling, URL advance) can engage mid-run instead of only
      // after the stream completes.
      try {
        onThreadCreated?.(thread_id);
      } catch (e) {
        console.warn('onThreadCreated callback threw:', e);
      }

      // Start streaming run
      console.log('LangGraphTenderAPI: Starting stream...');
      const streamResponse = client.runs.stream(
        thread_id,
        "agent", // This should match the assistant ID in langgraph.json
        {
          // CRITICAL: subscribe to all stream modes the hook consumes. Without
          // `values`, the SDK defaults to `updates` which only emits node names —
          // no state data — so useTenderAnalysis.ts's `values` case never fires,
          // `results` stays empty, and the UI shows "0 Questions" throughout a
          // live streaming run even though the backend is extracting questions.
          // This was the root cause of the "0 Questions during live streaming" bug.
          streamMode: ["values", "updates", "messages", "events"] as any,
          streamSubgraphs: true,
          input: {
            url: formData.url,
            successful_tender_urls: formData.successful_tender_urls,
            company_id: formData.company_id,
            project_id: formData.project_id,
            response_mode: formData.response_mode || 'generate',
          },
          config: {
            configurable: {
              user_id: userId || 'system',
              // Also pass thread_id explicitly so backend extract_thread_id
              // picks it up (it reads configurable.thread_id).
              thread_id,
            }
          }
        }
      );
      console.log('LangGraphTenderAPI: Stream started, processing chunks...');

      // Process the stream
      let finalState: TenderAnalysisState | null = null;
      let mergedState: Partial<TenderAnalysisState> = {};

      for await (const chunk of streamResponse) {
        if (!chunk.event) {
          continue;
        }

        // With streamSubgraphs:true the SDK namespaces event names as
        // `${event}|${subgraph_namespace}` (e.g. "values|response_workflow:abc").
        // Strip the namespace so downstream consumers match on the base event.
        const rawEvent = chunk.event as string;
        const pipeIdx = rawEvent.indexOf('|');
        const baseEvent = pipeIdx === -1 ? rawEvent : rawEvent.slice(0, pipeIdx);
        const subgraphNs = pipeIdx === -1 ? null : rawEvent.slice(pipeIdx + 1);

        if (baseEvent === 'values') {
          onEvent({
            event: 'values',
            data: chunk.data,
            metadata: subgraphNs ? { subgraph: subgraphNs } : undefined,
          });

          // Accumulate keys across parent + subgraph values events. A subgraph
          // values event carries only that subgraph's state slice, so a plain
          // overwrite would erase the parent's accumulated fields.
          if (chunk.data && typeof chunk.data === 'object') {
            mergedState = { ...mergedState, ...(chunk.data as Partial<TenderAnalysisState>) };
            if (Object.keys(mergedState).length > 0) {
              finalState = mergedState as TenderAnalysisState;
            }
          }
        } else if (baseEvent === 'messages') {
          onEvent({
            event: 'messages',
            data: chunk.data,
            metadata: subgraphNs ? { subgraph: subgraphNs } : undefined,
          });
        } else if (baseEvent === 'updates') {
          onEvent({
            event: 'updates',
            data: chunk.data,
            metadata: subgraphNs ? { subgraph: subgraphNs } : undefined,
          });
        } else if (baseEvent === 'events') {
          onEvent({
            event: 'events',
            data: chunk.data,
            metadata: subgraphNs ? { subgraph: subgraphNs } : undefined,
          });
        } else if (baseEvent === 'metadata') {
          console.log('Metadata received:', chunk.data);
        }
      }

      // Call completion with the final state after stream ends
      console.log('LangGraphTenderAPI: Stream ended, final state:', finalState);
      if (finalState) {
        console.log('LangGraphTenderAPI: Calling onComplete with final state');
        onComplete(finalState);
      } else {
        console.log('LangGraphTenderAPI: No final state, stream may have ended early');
      }

      console.log('LangGraphTenderAPI: Returning thread_id:', thread_id);
      return { thread_id };
    } catch (error) {
      console.error('LangGraphTenderAPI: Stream tender analysis error:', error);
      onError(error);
      throw error;
    }
  }

  /**
   * Get the current state of a thread, merging in any subgraph state.
   *
   * When the response_workflow subgraph holds canonical fields like
   * `refined_questions` and `drafted_questions`, the parent graph's
   * `state.values` does NOT carry them — the parent only sees what the
   * subgraph commits back, which on a paused run is empty (the subgraph
   * is mid-execution). Without subgraph state, the QuestionMatrix renders
   * "0 refined / 0 drafted" on page reload even though the run did refine
   * 10 and draft 1.
   *
   * Fix: walk the (subgraphs: true) state tree and shallow-merge any task's
   * subgraph values onto the parent values so callers see one flat payload.
   */
  static async getThreadState(threadId: string): Promise<TenderAnalysisState | null> {
    try {
      const state: any = await (client.threads as any).getState(threadId, undefined, {
        subgraphs: true,
      });

      const merged: Record<string, any> = { ...(state?.values || {}) };
      const visited = new Set<any>();
      const queue: any[] = [...(state?.tasks || [])];
      while (queue.length) {
        const task = queue.shift();
        if (!task || typeof task !== 'object' || visited.has(task)) continue;
        visited.add(task);
        const sub = task?.state;
        if (sub && typeof sub === 'object') {
          // Subgraph values override parent only where parent is empty —
          // the subgraph is the authoritative source for fields it owns
          // (refined_questions, drafted_questions, kb_examples_per_question,
          // past_tender_matches, quality_check_result, etc.).
          for (const [k, v] of Object.entries(sub.values || {})) {
            const prev = merged[k];
            const prevEmpty =
              prev === undefined ||
              prev === null ||
              (Array.isArray(prev) && prev.length === 0) ||
              (typeof prev === 'object' && !Array.isArray(prev) && Object.keys(prev).length === 0);
            if (prevEmpty) merged[k] = v;
          }
          for (const t of sub.tasks || []) queue.push(t);
        }
      }
      return (merged as unknown as TenderAnalysisState) || null;
    } catch (error) {
      console.error('Error getting thread state:', error);
      return null;
    }
  }

  /**
   * Check whether the thread is currently paused at a final-approval HITL
   * checkpoint. Returns the interrupt payload if so, else null.
   *
   * The langgraph backend pauses at `final_approval` via `interrupt(...)`;
   * the run sits in `interrupted` status until we send a resume command.
   * Without this check (and the corresponding resume call), the user sees
   * a "completed-looking" Refinement screen but Performance Analysis +
   * Final Report tabs never populate.
   */
  static async getPendingFinalApproval(threadId: string): Promise<any | null> {
    try {
      // The `final_approval` interrupt fires inside the `response_workflow`
      // subgraph (the response subgraph is composed into the parent agent
      // graph). Without `subgraphs: true`, getState returns only the parent
      // graph's tasks — which is empty when the parent's only outstanding
      // task is "the subgraph is paused." Always fetch with subgraphs so
      // we surface interrupts at any depth.
      // eslint-disable-next-line no-console
      console.log('[HITL] getPendingFinalApproval start', threadId);
      const state: any = await (client.threads as any).getState(threadId, undefined, {
        subgraphs: true,
      });
      // eslint-disable-next-line no-console
      console.log('[HITL] getState returned', {
        hasTasks: Array.isArray(state?.tasks) && state.tasks.length > 0,
        topLevelInterrupts: state?.interrupts ? Object.keys(state.interrupts).length : 0,
        next: state?.next,
      });

      // Walk every task in the (possibly nested) state. Each task may carry
      // its own interrupts; LangGraph also surfaces a top-level
      // `interrupts` map keyed by checkpoint id when paused — we walk both
      // shapes for resilience across SDK versions.
      const findInterrupt = (root: any): any | null => {
        const visited = new Set<any>();
        const queue: any[] = [root];
        while (queue.length) {
          const node = queue.shift();
          if (!node || typeof node !== 'object' || visited.has(node)) continue;
          visited.add(node);

          const tasks: any[] = node?.tasks || [];
          for (const task of tasks) {
            const interrupts: any[] = task?.interrupts || [];
            for (const intr of interrupts) {
              const value = intr?.value;
              if (value && (value.action === 'final_approval' || value.requires_approval)) {
                return value;
              }
            }
            // Subgraph state may live under `task.state` — recurse into it.
            if (task?.state) queue.push(task.state);
          }

          // Top-level `interrupts` map: { [checkpoint_id]: [{ id, value }] }
          const topInterrupts = node?.interrupts;
          if (topInterrupts && typeof topInterrupts === 'object') {
            for (const arr of Object.values(topInterrupts) as any[]) {
              if (!Array.isArray(arr)) continue;
              for (const intr of arr) {
                const value = intr?.value;
                if (value && (value.action === 'final_approval' || value.requires_approval)) {
                  return value;
                }
              }
            }
          }
        }
        return null;
      };

      const found = findInterrupt(state);
      // eslint-disable-next-line no-console
      console.log('[HITL] findInterrupt result:', found ? { action: found.action, requires_approval: found.requires_approval } : null);
      return found;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[HITL] Error checking pending final approval:', error);
      return null;
    }
  }

  /**
   * Resume a thread that's paused at the final-approval HITL checkpoint.
   *
   * Sends `Command(resume={"type": "approve"})` (or "reject" / "request_revision")
   * back into the workflow. The backend's `final_approval` node reads this
   * via `review_request.get("type", "approve")` and proceeds to the next
   * stage (compare_score_improvement → export_response_comparison → end).
   *
   * Streams the continuation events through the same callbacks the original
   * analysis used so the FE keeps consuming `values`/`updates` chunks for
   * the post-approval nodes (Performance Analysis + Final Report tabs).
   */
  static async resumeFinalApproval(
    threadId: string,
    decision: { type: 'approve' | 'reject' | 'request_revision'; edits?: Record<string, string>; reason?: string },
    onEvent: (event: StreamEvent) => void,
    onError: (error: any) => void,
    onComplete: (finalState: TenderAnalysisState) => void,
    userId?: string,
  ): Promise<void> {
    try {
      const streamResponse = (client.runs as any).stream(
        threadId,
        'agent',
        {
          streamMode: ['values', 'updates', 'messages', 'events'] as any,
          streamSubgraphs: true,
          // LangGraph SDK: pass `command.resume` to inject the human reply.
          // The backend's `interrupt(...)` returns this object verbatim.
          command: { resume: decision } as any,
          config: {
            configurable: {
              user_id: userId || 'system',
              thread_id: threadId,
            },
          },
        },
      );

      let mergedState: Partial<TenderAnalysisState> = {};
      let finalState: TenderAnalysisState | null = null;
      for await (const chunk of streamResponse as any) {
        if (!chunk?.event) continue;
        const rawEvent = chunk.event as string;
        const pipeIdx = rawEvent.indexOf('|');
        const baseEvent = pipeIdx === -1 ? rawEvent : rawEvent.slice(0, pipeIdx);
        const subgraphNs = pipeIdx === -1 ? null : rawEvent.slice(pipeIdx + 1);
        if (baseEvent === 'values' || baseEvent === 'updates' || baseEvent === 'messages' || baseEvent === 'events') {
          onEvent({ event: baseEvent, data: chunk.data, metadata: subgraphNs ? { subgraph: subgraphNs } : undefined });
          if (baseEvent === 'values' && chunk.data && typeof chunk.data === 'object') {
            mergedState = { ...mergedState, ...(chunk.data as Partial<TenderAnalysisState>) };
            if (Object.keys(mergedState).length > 0) {
              finalState = mergedState as TenderAnalysisState;
            }
          }
        }
      }
      if (finalState) onComplete(finalState);
    } catch (err) {
      console.error('Error resuming final approval:', err);
      onError(err);
      throw err;
    }
  }

  /**
   * Get thread history
   */
  static async getThreadHistory(threadId: string) {
    try {
      const history = await client.threads.getHistory(threadId);
      return history;
    } catch (error) {
      console.error('Error getting thread history:', error);
      return [];
    }
  }

  /**
   * Get available assistants/graphs
   */
  static async getAvailableAssistants() {
    try {
      const assistants = await client.assistants.search();
      return assistants;
    } catch (error) {
      console.error('Error getting assistants:', error);
      return [];
    }
  }

  /**
   * Health check
   */
  static async healthCheck(): Promise<boolean> {
    try {
      // Try to get assistants as a health check
      await this.getAvailableAssistants();
      return true;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}

export default LangGraphTenderAPI;